import React, { FC, useEffect, useMemo } from "react";

import { Body1, Card, CardHeader, Spinner, Title1 } from "@fluentui/react-components";
import {
  DocumentBulletList20Filled,
  DocumentError20Filled,
  NotebookEye20Filled,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";

import useStyles from "./styles";
import Button from "../../../../components/Button/Button";
import ProgressBar from "../../../../components/ProgressBar/ProgressBar";
import { useFuzzyScanning } from "../../../../hooks/useFuzzyScanning";
import { ResolutionStatus } from "../../../../models/benchly.models";
import { useCitationStore } from "../../../../store/CitationStore";

const ExecutiveSummary: FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { citations, isLoaded, isLoading, loadCitationsFromStorage } = useCitationStore();

  // Start fuzzy search for non-certified citations
  const { isSearching, progress: fuzzyProgress, statusMessage } = useFuzzyScanning();

  // If citations aren't loaded in context, try to load from document storage
  useEffect(() => {
    if (!isLoaded && !isLoading && citations.length === 0) {
      console.log("[ExecutiveSummary] Citations not in context, loading from storage...");
      loadCitationsFromStorage().then((loaded) => {
        if (loaded) {
          console.log("[ExecutiveSummary] Citations loaded from storage successfully");
        } else {
          console.log("[ExecutiveSummary] No citations found in storage");
        }
      });
    }
  }, [isLoaded, isLoading, citations.length, loadCitationsFromStorage]);

  // Calculate statistics from context
  const stats = useMemo(() => {
    const total = citations.length;

    const getEffectiveStatuses = (r: (typeof citations)[number]): ResolutionStatus[] => {
      // Prefer resolvedLawCase if user/system has selected a specific resolution
      if (r.resolvedLawCase && r.resolvedLawCase !== "none") {
        return [r.resolvedLawCase.status];
      }
      // Otherwise, consider all lawcases in this result
      return (r.citations ?? []).map((c) => c.status);
    };

    const hasAnyStatus = (r: (typeof citations)[number], statuses: ResolutionStatus[]) => {
      const effective = getEffectiveStatuses(r);
      return effective.some((s) => statuses.includes(s));
    };

    const verified = citations.filter((r) =>
      hasAnyStatus(r, [ResolutionStatus.ValidatedAndCertified])
    ).length;

    const needsVerification = citations.filter((r) =>
      hasAnyStatus(r, [ResolutionStatus.RequiresManualReview, ResolutionStatus.NeedsVerification])
    ).length;

    const unverified = citations.filter((r) => hasAnyStatus(r, [ResolutionStatus.NotFound])).length;

    return { total, verified, needsVerification, unverified };
  }, [citations]);

  // Show loading state while fetching citations
  if (isLoading) {
    return (
      <div className={styles.container}>
        <Title1>Executive Summary</Title1>
        <Body1>Loading citation data...</Body1>
        <Spinner size="medium" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Title1>Executive Summary</Title1>

      <Body1>Overview of legal citation analysis and compliance status.</Body1>

      {/* Fuzzy Search Progress Bar */}
      {isSearching && (
        <div className={styles.fuzzySearchContainer}>
          <Body1 className={styles.fuzzySearchTitle}>🔍 Running advanced fuzzy search...</Body1>
          <ProgressBar value={fuzzyProgress} loading={true} />
          <Body1 className={styles.fuzzySearchStatus}>{statusMessage}</Body1>
        </div>
      )}

      <div className={styles.summaryGrid}>
        <Card className={styles.statCard}>
          <CardHeader header={<Body1>Total Citations Found</Body1>} />
          <Body1 className={styles.statNumber}>{stats.total}</Body1>
        </Card>

        <Card className={styles.statCard}>
          <CardHeader header={<Body1>Verified Citations</Body1>} />
          <Body1 className={isSearching ? styles.pulsingNumber : styles.statNumber}>
            {stats.verified}
          </Body1>
        </Card>

        <Card className={styles.statCard}>
          <CardHeader header={<Body1>Needs Verification</Body1>} />
          <Body1 className={isSearching ? styles.pulsingNumber : styles.statNumber}>
            {stats.needsVerification}
          </Body1>
        </Card>

        <Card className={styles.statCard}>
          <CardHeader header={<Body1>Unverified Citations</Body1>} />
          <Body1 className={isSearching ? styles.pulsingNumber : styles.statNumber}>
            {stats.unverified}
          </Body1>
        </Card>
      </div>

      <div className={styles.buttonContainer}>
        <Button
          variant="primary"
          icon={<DocumentError20Filled />}
          onClick={() => navigate("/resolving")}
          disabled={isSearching}
        >
          {isSearching ? "Searching..." : "Begin Resolving"}
        </Button>

        <Button
          variant="primary"
          icon={<NotebookEye20Filled />}
          onClick={() => navigate("/view-cases")}
        >
          View Cited Cases
        </Button>

        <Button
          variant="primary"
          icon={<DocumentBulletList20Filled />}
          onClick={() => navigate("/certification-report")}
        >
          Certification Report
        </Button>
      </div>
    </div>
  );
};

export default ExecutiveSummary;
